import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Typography,
  Snackbar,
  Alert,
  Box,
  LinearProgress,
  Fade,
  Zoom,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useUser } from "./UserContext";
import "./UserLogin.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LoginForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { setUsername: setGlobalUsername } = useUser();

  const [username, setUsernameLocal] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [shakeUsername, setShakeUsername] = useState(false);
  const [shakePassword, setShakePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [formVisible, setFormVisible] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const [loginProgress, setLoginProgress] = useState(0);

  useEffect(() => {
    setFormVisible(true);
  }, []);

  useEffect(() => {
    setUsernameValid(username.length >= 4);
  }, [username]);

  useEffect(() => {
    setPasswordValid(password.length >= 4);
  }, [password]);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const validateField = (value, minLength, fieldName) => {
    if (value.length < minLength) {
      setSnackbar({
        open: true,
        message: `${fieldName} must be at least ${minLength} characters.`,
        severity: "warning",
      });
      return false;
    }
    return true;
  };

  const animateShake = (setter) => {
    setter(true);
    setTimeout(() => setter(false), 600);
  };

  const simulateProgress = () => {
    setLoginProgress(0);
    const interval = setInterval(() => {
      setLoginProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const isNoActiveSessionError = (error) => {
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "";
    const lowerMessage = message.toLowerCase();

    return (
      lowerMessage.includes("no active") ||
      lowerMessage.includes("no ongoing") ||
      lowerMessage.includes("not found") ||
      lowerMessage.includes("no session") ||
      lowerMessage.includes("session not found")
    );
  };

  const loginRequestWithFallback = async () => {
    
    const storedUsername = localStorage.getItem("username");
    const shouldTryAlterLogin = storedUsername === username;


    let response;
    let isAlterLoginUsed = false;

    if (shouldTryAlterLogin) {
      try {
        response = await axios.post(`${API_BASE_URL}/alterUserLogin`, {
          username,
          password,
        });
        const d = response.data;

        const isCleanSession =
          !d.userCategory &&
          !d.userpath &&
          !d.tableData &&
          d.message === "User is available";

        if (isCleanSession) {
          response = await axios.post(`${API_BASE_URL}/login`, {
            username,
            password,
          });
          isAlterLoginUsed = false;
        } else {
          isAlterLoginUsed = true;
        }
      } catch (alterError) {        
        if (isNoActiveSessionError(alterError)) {
          response = await axios.post(`${API_BASE_URL}/login`, {
            username,
            password,
          });
          isAlterLoginUsed = false;
        } else {
          throw alterError;
        }
      }
    } else {
      response = await axios.post(`${API_BASE_URL}/login`, {
        username,
        password,
      });
    }

    return { data: response.data, isAlterLoginUsed };
  };

  const redirectUser = (data) => {

    if (data.userCategory === "operator") {
      navigate("/ShiftDetails", { state: { username } });
    } else if (data.userCategory === "planner") {
      navigate("/PlanDashbord", { state: { username } });
    } else if (data.userCategory === "admin") {
      navigate("/AdminDashboard", { state: { username } });
    } else if (data.userCategory === "hr") {
      navigate("/HrDashbord", { state: { username } });
    } else if (data.userCategory === "machinesupervisor") {
      navigate("/MachineSupervisorPage", { state: { username } });
    } else if (data.userCategory === "supervisor") {
      
      if (data.machines && data.machines.length > 0) {
        navigate("/SupervisorMainPage", {
          state: {
            supervisorId: username,
            machines: data.machines,
          },
        });
      } else {
        navigate("/ManhourShiftEnter", { state: { username } });
      }
    } else if (data.userpath === "main categories") {
      navigate("/MainCategories", { state: { username } });
    } else if (data.tableData && data.tableData.length > 0) {
      const op = data.tableData[0];

      const adjustedJobStartedTime = new Date(op.JobStartedTime);
      adjustedJobStartedTime.setHours(adjustedJobStartedTime.getHours() - 5);
      adjustedJobStartedTime.setMinutes(
        adjustedJobStartedTime.getMinutes() - 30
      );

      const commonState = {
        username,
        jobNumber: op.job_number,
        customer: op.customer,
        product: op.product,
        operation: op.main_operation,
        startedTime: adjustedJobStartedTime.toISOString(),
        machineCode: op.machine_code,
        operation_code: op.operation_code,
        operation_description: op.operation_description,
        start_datetime: op.start_datetime,
        employee_id: op.employee_id,
        operation_type: op.operation_type,
      };

      if (op.active_status === "Finished") {
        if (op.operation_type?.startsWith("General")) {
          navigate("/GeneralProcess", { state: { username } });
        } else if (op.operation_type === "JOB_END") {
          navigate("/JobProcessSelection", { state: { username } });
        } else {
          navigate("/JobOperation", { state: commonState });
        }
      } else if (op.active_status === "Ongoing") {
        if (op.operation_type?.startsWith("General")) {
          navigate("/GeneralProcess", { state: commonState });
        } else {
          navigate("/JobOperation", { state: commonState });
        }
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateField(username, 4, "Username")) {
      animateShake(setShakeUsername);
      return;
    }

    if (!validateField(password, 4, "Password")) {
      animateShake(setShakePassword);
      return;
    }

    try {
      setLoading(true);
      simulateProgress();

      const { data, isAlterLoginUsed } = await loginRequestWithFallback();

      if (data.userCategory || data.userpath || data.tableData) {
        setUsernameLocal(username);
        setGlobalUsername(username);
        localStorage.setItem("username", username);
        sessionStorage.setItem("username", username);

        setSnackbar({
          open: true,
          message: isAlterLoginUsed
            ? "Session resumed! Redirecting..."
            : "Login successful! Redirecting...",
          severity: "success",
        });


        setTimeout(() => {
          redirectUser(data);
        }, 1000);
      } else {

        localStorage.removeItem("username");

        setSnackbar({
          open: true,
          message:
            "Invalid credentials. Please check your username and password.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Login error:", error);

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.error || "Login failed. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setLoginProgress(0);
    }
  };

  useEffect(() => {
    const preventBack = () => {
      window.history.pushState(null, document.title, window.location.href);
      alert(
        "You can't go back from this window-කණගාටුයි! ඔබට මෙම පේජ් එකෙන් පසුපසට යා නොහැක."
      );
    };

    window.addEventListener("popstate", preventBack);
    window.history.pushState(null, document.title, window.location.href);

    return () => window.removeEventListener("popstate", preventBack);
  }, []);

  return (
    <div className="banner">
      <Container
        maxWidth="xs"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          padding: isMobile ? 2 : 3,
        }}
      >
        <Zoom in={formVisible} timeout={800}>
          <Card
            className="loginform"
            sx={{
              padding: isMobile ? 3 : 5,
              borderRadius: "24px",
              backgroundColor: "rgba(30, 30, 30, 0.85)",
              boxShadow: "0 25px 45px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(20px)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {loading && (
              <LinearProgress
                variant="determinate"
                value={loginProgress}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  borderRadius: "24px 24px 0 0",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #667eea, #764ba2)",
                  },
                }}
              />
            )}

            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Fade in={formVisible} timeout={1200}>
                <SecurityIcon
                  sx={{
                    fontSize: 48,
                    color: "#667eea",
                    mb: 1,
                    filter:
                      "drop-shadow(0 4px 8px rgba(102, 126, 234, 0.3))",
                  }}
                />
              </Fade>

              <Typography
                variant={isMobile ? "h5" : "h4"}
                align="center"
                fontWeight="700"
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 60%, #ff6b6b 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                  animation: "gentlePulse 4s ease-in-out infinite",
                }}
              >
                Welcome back
              </Typography>

              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "rgba(255, 255, 255, 0.85)",
                  fontWeight: 400,
                }}
              >
                Sign in to continue
              </Typography>
            </Box>

            <Divider
              sx={{
                mb: 4,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                height: 1,
              }}
            />

            <form onSubmit={handleSubmit}>
              <Box sx={{ position: "relative", mb: 3 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Username"
                  value={username}
                  onChange={(e) => setUsernameLocal(e.target.value)}
                  onFocus={() => setFocusedField("username")}
                  onBlur={() => setFocusedField("")}
                  variant="outlined"
                  className={shakeUsername ? "shake" : ""}
                  inputProps={{ maxLength: 15 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon
                          sx={{
                            color:
                              focusedField === "username"
                                ? "#667eea"
                                : "rgba(255, 255, 255, 0.7)",
                            transition: "color 0.3s ease",
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: usernameValid && (
                      <InputAdornment position="end">
                        <Fade in={usernameValid}>
                          <CheckCircleIcon
                            sx={{ color: "#4caf50", fontSize: 20 }}
                          />
                        </Fade>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        borderColor: "#667eea",
                        boxShadow: "0 0 20px rgba(102, 126, 234, 0.3)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-focused": {
                        color: "#667eea",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      color: "white",
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>

              <Box sx={{ position: "relative", mb: 4 }}>
                <TextField
                  fullWidth
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField("")}
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  className={shakePassword ? "shake" : ""}
                  inputProps={{ maxLength: 20 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{
                            color:
                              focusedField === "password"
                                ? "#667eea"
                                : "rgba(255, 255, 255, 0.7)",
                            transition: "color 0.3s ease",
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {passwordValid && (
                            <Fade in={passwordValid}>
                              <CheckCircleIcon
                                sx={{ color: "#4caf50", fontSize: 20 }}
                              />
                            </Fade>
                          )}
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              "&:hover": {
                                color: "#667eea",
                                backgroundColor:
                                  "rgba(102, 126, 234, 0.1)",
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        borderColor: "#667eea",
                        boxShadow: "0 0 20px rgba(102, 126, 234, 0.3)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255, 255, 255, 0.7)",
                      "&.Mui-focused": {
                        color: "#667eea",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      color: "white",
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !usernameValid || !passwordValid}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <LoginIcon />
                  )
                }
                sx={{
                  borderRadius: "16px",
                  py: 1.8,
                  fontWeight: "600",
                  fontSize: "1.1rem",
                  textTransform: "none",
                  background:
                    usernameValid && passwordValid
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #9e9e9e 0%, #757575 100%)",
                  boxShadow:
                    usernameValid && passwordValid
                      ? "0 8px 25px rgba(102, 126, 234, 0.4)"
                      : "0 4px 15px rgba(158, 158, 158, 0.3)",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover:not(:disabled)": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.5)",
                    background:
                      "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                  },
                }}
              >
                {loading ? "Signing you in..." : "Sign In"}
              </Button>
            </form>
          </Card>
        </Zoom>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleSnackbarClose}
          sx={{
            borderRadius: "12px",
            fontWeight: 500,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style jsx>{`
        .shake {
          animation: shake 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97);
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-4px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(4px);
          }
        }
      `}</style>
    </div>
  );
};

export default LoginForm;